import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { QuillModule } from 'ngx-quill';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AnnouncementsService } from '../services/announcements.service';
import { AnnouncementNotificationService } from '../services/announcement-notification.service';
import { Announcement } from '../models/announcement.models';
import { LoggerService } from '../../../shared/services/logger.service';
import { SnackBarService } from '../../../shared/services/snack-service.service';
import { AnnouncementAttachmentsComponent } from '../announcement-attachments/announcement-attachments.component';

export interface AnnouncementGateDialogData {
  announcementId: number;
  /** 1-based position of this item within the current blocking batch. Drives GATE.PROGRESS. */
  index?: number;
  /** Total items in the current blocking batch. */
  total?: number;
}

/**
 * Content of the blocking High-priority dialog, opened by `AnnouncementGateComponent`.
 *
 * Shows one item at a time. On success it re-fetches the attention summary itself and
 * either loads the next blocking item in place or closes — the gate only ever opens
 * the dialog once per session-worthy batch; this component owns advancing through it.
 *
 * Never traps the user on a network failure: a failed load or a failed confirm keeps a
 * retry available, and after a second consecutive failure also offers "Sulje
 * toistaiseksi" — closes without marking anything seen/acknowledged. `dialogRef.close(true)`
 * signals that deferral to `AnnouncementGateComponent`, which re-arms itself to reopen
 * on the next navigation (see its `afterClosed()` handling).
 */
@Component({
  selector: 'ri-announcement-gate-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslatePipe,
    QuillModule,
    AnnouncementAttachmentsComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './announcement-gate-dialog.component.html',
  styleUrls: ['./announcement-gate-dialog.component.scss']
})
export class AnnouncementGateDialogComponent implements OnInit, OnDestroy {
  announcement: Announcement | null = null;
  isLoading = true;
  isSubmitting = false;
  loadError = false;
  confirmError = false;
  /** 1-based position / total size of the current blocking batch, for GATE.PROGRESS. */
  index = 1;
  total = 1;

  private readonly destroy$ = new Subject<void>();
  private lastRequestedId!: number;
  private loadFailureCount = 0;
  private confirmFailureCount = 0;

  constructor(
    private readonly dialogRef: MatDialogRef<AnnouncementGateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private readonly data: AnnouncementGateDialogData,
    private readonly announcementsService: AnnouncementsService,
    private readonly notifications: AnnouncementNotificationService,
    private readonly snackBar: SnackBarService,
    private readonly logger: LoggerService,
    private readonly cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.index = this.data.index ?? 1;
    this.total = this.data.total ?? 1;
    this.loadItem(this.data.announcementId);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get cleanBody(): string {
    return (this.announcement?.body ?? '').replace(/&nbsp;/g, ' ');
  }

  /** After a second consecutive load failure, offer a way out besides retrying forever. */
  get canCloseAfterLoadError(): boolean {
    return this.loadFailureCount >= 2;
  }

  /** After a second consecutive confirm failure, offer a way out besides retrying forever. */
  get canCloseAfterConfirmError(): boolean {
    return this.confirmFailureCount >= 2;
  }

  onConfirm(): void {
    if (!this.announcement || this.isSubmitting) return;

    this.isSubmitting = true;
    this.confirmError = false;
    this.cdr.markForCheck();

    const action$ = this.announcement.requiresAcknowledgment
      ? this.announcementsService.acknowledge(this.announcement.id)
      : this.announcementsService.markAsSeen(this.announcement.id);

    action$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.confirmFailureCount = 0;
        this.advance();
      },
      error: error => {
        this.logger.errorWithContext(
          'AnnouncementGateDialogComponent', 'Failed to confirm blocking announcement', error);
        this.snackBar.error('ANNOUNCEMENTS.GATE.CONFIRM_ERROR', { translate: true });
        this.isSubmitting = false;
        this.confirmError = true;
        this.confirmFailureCount++;
        this.cdr.markForCheck();
      }
    });
  }

  /** Retries the item that just failed to load — same id, failure count preserved. */
  onRetryLoad(): void {
    this.loadItem(this.lastRequestedId, true);
  }

  /**
   * Closes the dialog WITHOUT marking anything seen or acknowledged — the escape hatch
   * offered after a second consecutive load or confirm failure. `AnnouncementGateComponent`
   * reads the truthy close result and re-arms itself so the same item reopens the gate
   * at the next navigation instead of being silently lost.
   */
  onCloseTemporarily(): void {
    this.dialogRef.close(true);
  }

  /** `isRetry` preserves the running failure count instead of resetting it for a new item. */
  private loadItem(id: number, isRetry = false): void {
    this.lastRequestedId = id;
    this.isLoading = true;
    this.loadError = false;
    if (!isRetry) {
      this.loadFailureCount = 0;
    }
    this.cdr.markForCheck();

    this.announcementsService.getAnnouncementById(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: announcement => {
        this.announcement = announcement;
        this.isLoading = false;
        this.isSubmitting = false;
        this.loadError = false;
        this.loadFailureCount = 0;
        this.cdr.markForCheck();
      },
      error: error => {
        this.logger.errorWithContext(
          'AnnouncementGateDialogComponent', 'Failed to load blocking announcement', error);
        this.snackBar.error('ANNOUNCEMENTS.GATE.LOAD_ERROR', { translate: true });
        this.isLoading = false;
        this.loadError = true;
        this.loadFailureCount++;
        this.cdr.markForCheck();
      }
    });
  }

  /** Refreshes the shared bell/banner summary, then shows the next blocking item or closes. */
  private advance(): void {
    this.notifications.refresh();

    this.announcementsService.getAttention().pipe(takeUntil(this.destroy$)).subscribe({
      next: attention => {
        const next = attention.blocking[0];
        if (next) {
          // Self-correcting: reflects both items confirmed since open and any new
          // High-priority arrival that grew the batch mid-session.
          this.total = this.index + attention.blocking.length;
          this.index++;
          this.loadItem(next.id);
        } else {
          this.dialogRef.close();
        }
      },
      error: error => {
        this.logger.errorWithContext(
          'AnnouncementGateDialogComponent', 'Failed to load the next blocking announcement', error);
        this.dialogRef.close();
      }
    });
  }
}
